import React, { useState, useRef, useCallback, useEffect } from "react";
import Typography from "../../../Foundation/Typography/Typography";
import VideoSlide from "./VideoSlide/VideoSlide";
import {
  _LandingVideoSliderAdrienCachotRoot,
  _LandingVideoSliderAdrienCachotTrack,
  _LandingVideoSliderAdrienCachotTrackItem,
  _LandingVideoSliderAdrienCachotDotsContainer,
  _LandingVideoSliderAdrienCachotDot,
  _LandingVideoSliderAdrienCachotArrow,
} from "./LandingVideoSliderAdrienCachot.styled";
import StyledDiv from "../../StyledDiv/StyledDiv";
import Icon from "../../../Foundation/Icon/Icon";

export interface IVideoSliderItem {
  title?: string;
  /** ex: "Épisode 1", "Épisode 2" */
  subtitle?: string;
  coverImage?: string;
  /** Video slug (SushishopVideo) */
  videoSlug?: string;
  videoSlugMobile?: string;
  /** ISO date string — si dans le futur, la vidéo n'est pas encore disponible */
  availableDate?: string;
}

export interface ILandingVideoSliderAdrienCachotProps {
  title?: string;
  videos?: IVideoSliderItem[];
  watchNowLabel?: string;
  newEpisodeLabel?: string;
  /** Affichage de la date d’épisode (ex. moment côté app). Sinon fallback fr-FR dans VideoSlide. */
  formatAvailableDateDisplay?: (dateIso: string) => string;
  renderVideo?: (videoSlug: string, onEnded: () => void) => React.ReactNode;
  /** Sur mobile : ouvre la vidéo dans le player natif en plein écran */
  onMobilePlay?: (videoSlug: string | undefined) => void;
}

const LandingVideoSliderAdrienCachot = (
  props: ILandingVideoSliderAdrienCachotProps
) => {
  const {
    title,
    videos = [],
    watchNowLabel = "Regardez dès maintenant",
    newEpisodeLabel = "Nouvel épisode le",
    formatAvailableDateDisplay,
    renderVideo,
    onMobilePlay,
  } = props;

  const n = videos.length;
  const hasLoop = n > 1;

  // Extended array: [clone_last, v1, v2, ..., vn, clone_first]
  const extendedVideos = hasLoop
    ? [videos[n - 1], ...videos, videos[0]]
    : videos;
  const totalExt = extendedVideos.length;

  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Track current extended index without triggering re-renders
  const extIndexRef = useRef(hasLoop ? 1 : 0);

  const scrollToExtended = useCallback((extIdx: number, instant = false) => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.querySelector<HTMLElement>(
      `[data-ext-index="${extIdx}"]`
    );
    if (!item) return;
    const scrollLeft =
      item.offsetLeft + item.offsetWidth / 2 - track.clientWidth / 2;
    if (instant) {
      track.scrollLeft = scrollLeft;
    } else {
      track.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  }, []);

  // Center at first real slide on mount
  useEffect(() => {
    scrollToExtended(hasLoop ? 1 : 0, true);
  }, [hasLoop, scrollToExtended]);

  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const items = track.querySelectorAll<HTMLElement>("[data-ext-index]");
    const centerX = track.scrollLeft + track.clientWidth / 2;
    let closestExtIdx = hasLoop ? 1 : 0;
    let minDist = Infinity;
    items.forEach((item) => {
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const dist = Math.abs(centerX - itemCenter);
      if (dist < minDist) {
        minDist = dist;
        closestExtIdx = Number(item.dataset.extIndex);
      }
    });

    // Update active dot index
    if (hasLoop) {
      if (closestExtIdx === 0) {
        setActiveIndex(n - 1);
      } else if (closestExtIdx === totalExt - 1) {
        setActiveIndex(0);
      } else {
        setActiveIndex(closestExtIdx - 1);
      }
    } else {
      setActiveIndex(closestExtIdx);
    }
    extIndexRef.current = closestExtIdx;

    // After scroll settles, jump from clone to real counterpart (invisible)
    clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = setTimeout(() => {
      if (!hasLoop) return;
      const extIdx = extIndexRef.current;
      if (extIdx === 0) {
        // Clone of last → instant jump to real last
        scrollToExtended(totalExt - 2, true);
      } else if (extIdx === totalExt - 1) {
        // Clone of first → instant jump to real first
        scrollToExtended(1, true);
      }
    }, 80);
  }, [hasLoop, n, totalExt, scrollToExtended]);

  const goNext = useCallback(() => {
    const next = Math.min(extIndexRef.current + 1, totalExt - 1);
    scrollToExtended(next);
  }, [scrollToExtended, totalExt]);

  const goPrev = useCallback(() => {
    const prev = Math.max(extIndexRef.current - 1, 0);
    scrollToExtended(prev);
  }, [scrollToExtended]);

  // Dot click → map real index to extended index
  const scrollToSlide = useCallback(
    (realIndex: number) => {
      scrollToExtended(hasLoop ? realIndex + 1 : realIndex);
    },
    [hasLoop, scrollToExtended]
  );

  return (
    <StyledDiv
      styles={{
        position: "relative",
        width: "100%",
        mobile: {
          paddingBottom: 30,
        },
      }}
      className="landing-video-slider-adrien-cachot"
    >
      {title && (
        <_LandingVideoSliderAdrienCachotRoot>
          <StyledDiv
            styles={{
              width: "100%",
              maxWidth: 1120,
              margin: "0 auto",
              marginBottom: "54px",
              mobile: {
                marginBottom: "30px",
              },
            }}
          >
            <StyledDiv
              as={Typography}
              variant="adriencachot3"
              color="white"
              styles={{
                maxWidth: 351,
                mobile: {
                  maxWidth: 265,
                },
              }}
            >
              {title}
            </StyledDiv>
          </StyledDiv>
        </_LandingVideoSliderAdrienCachotRoot>
      )}

      <StyledDiv styles={{ position: "relative" }}>
        {hasLoop && (
          <_LandingVideoSliderAdrienCachotArrow $side="left" onClick={goPrev} aria-label="Précédent">
            <Icon
              icon="caret_right_16"
              style={{ transform: "rotate(180deg)" }}
              color="#fff"
              size={32}
            />
          </_LandingVideoSliderAdrienCachotArrow>
        )}

        {hasLoop && (
          <_LandingVideoSliderAdrienCachotArrow $side="right" onClick={goNext} aria-label="Suivant">
            <Icon icon="caret_right_16" color="#fff" size={32} />
          </_LandingVideoSliderAdrienCachotArrow>
        )}

        <_LandingVideoSliderAdrienCachotTrack
          ref={trackRef}
          onScroll={handleScroll}
        >
        {extendedVideos.map((video, extIdx) => (
          <_LandingVideoSliderAdrienCachotTrackItem
            key={extIdx}
            data-ext-index={String(extIdx)}
          >
            <VideoSlide
              {...video}
              isActive={extIdx === (hasLoop ? activeIndex + 1 : activeIndex)}
              watchNowLabel={watchNowLabel}
              newEpisodeLabel={newEpisodeLabel}
              formatAvailableDateDisplay={formatAvailableDateDisplay}
              renderVideo={renderVideo}
              onMobilePlay={
                onMobilePlay && (video.videoSlugMobile || video.videoSlug)
                  ? () => onMobilePlay(video.videoSlugMobile || video.videoSlug)
                  : undefined
              }
            />
          </_LandingVideoSliderAdrienCachotTrackItem>
        ))}
        </_LandingVideoSliderAdrienCachotTrack>
      </StyledDiv>

      {videos.length > 1 && (
        <_LandingVideoSliderAdrienCachotRoot>
          <_LandingVideoSliderAdrienCachotDotsContainer>
            {videos.map((_, i) => (
              <_LandingVideoSliderAdrienCachotDot
                key={i}
                $active={i === activeIndex}
                onClick={() => scrollToSlide(i)}
                aria-label={`Épisode ${i + 1}`}
              />
            ))}
          </_LandingVideoSliderAdrienCachotDotsContainer>
        </_LandingVideoSliderAdrienCachotRoot>
      )}
    </StyledDiv>
  );
};

export default LandingVideoSliderAdrienCachot;
